import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import autoBind from 'react-autobind';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import loadable from '@utils/loadable';

import Breadcrumbs from '../../Breadcrumbs';
import Preloader from '../../Preloader';

import TopicsSvc from '../../../services/TopicsSvc';
import SectorsSvc from '../../../services/SectorsSvc';
import LocationsSvc from '../../../services/dbServices/LocationsSvc';
import withComponentName from '../../../decorators/withComponentName';

import {
  SEO_BREADCRUMBS,
  TOPIC_CLASS_BREADCRUMBS,
  SECTOR_BREADCRUMBS,
  LOCATION_BREADCRUMBS,
  TOPIC_CLASS_PATH_ITEM,
  SECTORS_PATH_ITEM,
  LOCATIONS_PATH_ITEM,
} from '../../../data/directory/constants';
import {
  TOPIC_CLASS,
  SECTOR_TOPIC_CLASS,
  INDUSTRY_TOPIC_CLASS,
  SUB_INDUSTRY_TOPIC_CLASS,
  CATEGORY_TOPIC_CLASS,
  INDUSTRY_ONLY,
  SUB_INDUSTRY_ONLY,
  CATEGORY_ONLY,
  INDUSTRY,
  SUB_INDUSTRY,
  CATEGORY,
  LOCATION,
  CONTINENT,
  SUBCONTINENT,
  COUNTRY,
  STATE,
  CITY,
  AREA,
  PLACE,
  LOCATION_TYPES,
} from '../../../data/directory/topicTypes';

import { getQueryStringParams } from '../../../helpers/helpers';
import { getLocationParentData } from '../../../helpers/locationHelpers';
import { addPathOnBreadcrumbs } from '../../../helpers/topicsHelpers';

import Styles from './styles.module.scss';

const SlugsListHeader = loadable(() => import('./SlugsListHeader'));
const SlugsListBody = loadable(() => import('./SlugsListBody'));

const cx = classNames.bind(Styles);

const ITEMS_IN_PER_PAGE = 200;

export class TopicClassSlugs extends PureComponent {
  constructor(props) {
    super(props);
    autoBind(this);

    this.state = {
      slug: null,
      assets: null,
      alphabetHeaders: null,
      breadcrumbs: [],
      filter: null,
      pageNumber: 1,
      totalPages: 1,
      totalItems: 0,
      loading: true,
    };

    this.parentTopic = {
      name: '',
      slug: '',
    };
    this.sector = null;
    this.industry = null;
    this.subIndustry = null;
    this.category = null;
    this.topicMethod = null;
  }

  componentDidMount() {
    this.mounted = true;
    this.prepareData();
  }

  componentDidUpdate(prevProps) {
    const { queryString, slugType, slugName } = this.props;
    const prevSearch = getQueryStringParams(prevProps.queryString);
    const currentSearch = getQueryStringParams(queryString);

    if (
      prevProps.slugType !== slugType
      || prevSearch.filter !== currentSearch.filter || prevProps.slugName !== slugName
    ) {
      this.prepareData();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  async onPageSelect(page) {
    this.setState({
      loading: true,
    });

    const { filter } = this.state;
    const { slug, assets, totalPages } = await this.topicMethod({
      id: this.parentTopic.slug,
      page,
      per_page: ITEMS_IN_PER_PAGE,
      prefix: filter,
    });

    if (this.mounted) {
      this.setState({
        slug,
        assets,
        totalPages,
        pageNumber: page,
        loading: false,
      });
    }
  }

  async getTopicClassData(slugName) {
    this.parentTopic = await TopicsSvc.getTopicClassBySlug(slugName);

    return [
      TopicsSvc.getTopicClassSlugs.bind(TopicsSvc),
      TopicsSvc.getTopicClassAlphabetHeaders.bind(TopicsSvc),
      TOPIC_CLASS_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            {
              name: this.parentTopic.title,
              path: this.parentTopic.slug,
            },
          ],
          TOPIC_CLASS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getSectorTopicClassData(slugName, sectorSlug) {
    ([this.sector, this.parentTopic] = await Promise.all([
      SectorsSvc.getSector(sectorSlug),
      TopicsSvc.getTopicClassBySlug(slugName),
    ]));

    return [
      TopicsSvc.getTopicsBySectorsAndAssetClass.bind(TopicsSvc),
      TopicsSvc.getTopicClassAndSectorsAlphabetHeaders.bind(TopicsSvc),
      SECTOR_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            {
              name: this.sector.name,
              path: this.sector.slug,
            },
            {
              name: this.parentTopic.title,
              path: `${this.sector.slug}/${this.parentTopic.slug}`,
            },
          ],
          SECTORS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getIndustryTopicClassData(slugName, sectorSlug, industrySlug) {
    ([this.sector, this.industry, this.parentTopic] = await Promise.all([
      SectorsSvc.getSector(sectorSlug),
      SectorsSvc.getIndustry(industrySlug),
      TopicsSvc.getTopicClassBySlug(slugName),
    ]));

    return [
      TopicsSvc.getTopicsByIndustriesAndAssetClass.bind(TopicsSvc),
      TopicsSvc.getTopicClassAndIndustriesAlphabetHeaders.bind(TopicsSvc),
      SECTOR_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            {
              name: this.sector.name,
              path: this.sector.slug,
            },
            {
              name: this.industry.name,
              path: `${this.sector.slug}/${this.industry.slug}`,
            },
            {
              name: this.parentTopic.title,
              path: `${this.sector.slug}/${this.industry.slug}/${this.parentTopic.slug}`,
            },
          ],
          SECTORS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getSubIndustryTopicClassData(slugName, sectorSlug, industrySlug, subIndustrySlug) {
    ([this.sector, this.industry, this.subIndustry, this.parentTopic] = await Promise.all([
      SectorsSvc.getSector(sectorSlug),
      SectorsSvc.getIndustry(industrySlug),
      SectorsSvc.getSubIndustry(subIndustrySlug),
      TopicsSvc.getTopicClassBySlug(slugName),
    ]));

    return [
      TopicsSvc.getTopicsBySubIndustriesAndAssetClass.bind(TopicsSvc),
      TopicsSvc.getTopicClassAndSubIndustriesAlphabetHeaders.bind(TopicsSvc),
      SECTOR_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            {
              name: this.sector.name,
              path: this.sector.slug,
            },
            {
              name: this.industry.name,
              path: `${this.sector.slug}/${this.industry.slug}`,
            },
            {
              name: this.subIndustry.name,
              path: `${this.sector.slug}/${this.industry.slug}/${this.subIndustry.slug}`,
            },
            {
              name: this.parentTopic.title,
              path: `
                ${this.sector.slug}/${this.industry.slug}/${this.subIndustry.slug}/${this.parentTopic.slug}
              `.trim(),
            },
          ],
          SECTORS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getCategoryTopicClassData(
    slugName,
    sectorSlug,
    industrySlug,
    subIndustrySlug,
    startupCategorySlug,
  ) {
    ([
      this.sector,
      this.industry,
      this.subIndustry,
      this.category,
      this.parentTopic,
    ] = await Promise.all([
      SectorsSvc.getSector(sectorSlug),
      SectorsSvc.getIndustry(industrySlug),
      SectorsSvc.getSubIndustry(subIndustrySlug),
      SectorsSvc.getCategory(startupCategorySlug),
      TopicsSvc.getTopicClassBySlug(slugName),
    ]));

    return [
      TopicsSvc.getTopicsByCategoriesAndAssetClass.bind(TopicsSvc),
      TopicsSvc.getTopicClassAndCategoriesAlphabetHeaders.bind(TopicsSvc),
      SECTOR_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            {
              name: this.sector.name,
              path: this.sector.slug,
            },
            {
              name: this.industry.name,
              path: `${this.sector.slug}/${this.industry.slug}`,
            },
            {
              name: this.subIndustry.name,
              path: `${this.sector.slug}/${this.industry.slug}/${this.subIndustry.slug}`,
            },
            {
              name: this.category.name,
              path: `${this.sector.slug}/${this.industry.slug}/${this.subIndustry.slug}/${this.category.slug}`,
            },
            {
              name: this.parentTopic.title,
              path: `
                ${this.sector.slug}/${this.industry.slug}/${this.subIndustry.slug}/${this.category.slug}/${this.parentTopic.slug}
              `.trim(),
            },
          ],
          SECTORS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getIndustryOnlyData() {
    return [
      SectorsSvc.getIndustries.bind(SectorsSvc),
      SectorsSvc.getIndustriesAlphabetHeaders.bind(SectorsSvc),
      SECTOR_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            {
              name: 'Industries',
              path: 'industries',
            },
          ],
          SECTORS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getSubIndustryOnlyData() {
    return [
      SectorsSvc.getSubIndustries.bind(SectorsSvc),
      SectorsSvc.getSubIndustriesAlphabetHeaders.bind(SectorsSvc),
      SECTOR_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            {
              name: 'Industries',
              path: 'industries',
            },
            {
              name: 'Subindustries',
              path: 'industries/subindustries',
            },
          ],
          SECTORS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getCategoryOnlyData() {
    return [
      SectorsSvc.getCategories.bind(SectorsSvc),
      SectorsSvc.getCategoriesAlphabetHeaders.bind(SectorsSvc),
      SEO_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            // TODO: show breadcrums or delete in case of redesign
            // {
            //   name: 'Industries',
            //   path: 'industries',
            // },
            // {
            //   name: 'Subindustries',
            //   path: 'industries/subindustries',
            // },
            {
              name: 'Categories',
              path: 'industries/subindustries/categories',
            },
          ],
          SECTORS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getIndustryData(slugName) {
    this.parentTopic = await SectorsSvc.getSector(slugName);

    return [
      SectorsSvc.getIndustries.bind(SectorsSvc),
      SectorsSvc.getIndustriesAlphabetHeaders.bind(SectorsSvc),
      SECTOR_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            {
              name: this.parentTopic.name,
              path: this.parentTopic.slug,
            },
            {
              name: 'Industries',
              path: '#',
            },
          ],
          SECTORS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getSubIndustryData(slugName, sectorSlug) {
    let sector;
    ([this.parentTopic, sector] = await Promise.all([
      SectorsSvc.getIndustry(slugName),
      SectorsSvc.getSector(sectorSlug),
    ]));

    return [
      SectorsSvc.getSubIndustries.bind(SectorsSvc),
      SectorsSvc.getSubIndustriesAlphabetHeaders.bind(SectorsSvc),
      SECTOR_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            {
              name: sector.name,
              path: sector.slug,
            },
            {
              name: this.parentTopic.name,
              path: `${sector.slug}/${this.parentTopic.slug}`,
            },
            {
              name: 'Subindustries',
              path: '#',
            },
          ],
          SECTORS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getCategoryData(slugName, sectorSlug, industrySlug) {
    let sector;
    let industry;
    ([this.parentTopic, sector, industry] = await Promise.all([
      SectorsSvc.getSubIndustry(slugName),
      SectorsSvc.getSector(sectorSlug),
      SectorsSvc.getIndustry(industrySlug),
    ]));

    return [
      SectorsSvc.getCategories.bind(SectorsSvc),
      SectorsSvc.getCategoriesAlphabetHeaders.bind(SectorsSvc),
      SECTOR_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            {
              name: sector.name,
              path: sector.slug,
            },
            {
              name: industry.name,
              path: `${sector.slug}/${industry.slug}`,
            },
            {
              name: this.parentTopic.name,
              path: `${sector.slug}/${industry.slug}/${this.parentTopic.slug}`,
            },
            {
              name: 'Categories',
              path: '#',
            },
          ],
          SECTORS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getLocationData() {
    this.parentTopic = {
      name: 'Locations',
      slug: LOCATIONS_PATH_ITEM,
    };

    return [
      LocationsSvc.getCountries.bind(LocationsSvc),
      LocationsSvc.getCountriesAlphabetHeaders.bind(LocationsSvc),
      LOCATION_BREADCRUMBS,
    ];
  }

  getParentTopic() {
    const { slugName, parentType } = this.props;

    switch (parentType) {
      case 'continent':
        return LocationsSvc.getContinent(slugName);
      case 'subcontinent':
        return LocationsSvc.getSubContinent(slugName);
      case 'country':
        return LocationsSvc.getCountry(slugName);
      case 'state':
        return LocationsSvc.getState(slugName);
      case 'city':
        return LocationsSvc.getCity(slugName);
      case 'area':
        return LocationsSvc.getArea(slugName);
      case 'place':
        return LocationsSvc.getPlace(slugName);
      default:
        return null;
    }
  }

  async getSubContinentData() {
    const { slugName, parentType } = this.props;
    this.parentTopic = await this.getParentTopic();

    const { breadcrumbs } = getLocationParentData(this.parentTopic, `${parentType}/${slugName}`);

    return [
      LocationsSvc.getSubContinents.bind(LocationsSvc),
      LocationsSvc.getSubContinentsAlphabetHeaders.bind(LocationsSvc),
      LOCATION_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            ...breadcrumbs,
            {
              name: 'Regions',
              path: window.location.pathname,
            },
          ],
          LOCATIONS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getCountryData() {
    const { slugName, parentType } = this.props;
    this.parentTopic = await this.getParentTopic();

    const { breadcrumbs } = getLocationParentData(this.parentTopic, `${parentType}/${slugName}`);

    return [
      LocationsSvc.getCountries.bind(LocationsSvc),
      LocationsSvc.getCountriesAlphabetHeaders.bind(LocationsSvc),
      LOCATION_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            ...breadcrumbs,
            {
              name: 'Countries',
              path: window.location.pathname,
            },
          ],
          LOCATIONS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getStateData() {
    const { slugName, parentType } = this.props;
    this.parentTopic = await this.getParentTopic();

    const { breadcrumbs } = getLocationParentData(this.parentTopic, `${parentType}/${slugName}`);

    return [
      LocationsSvc.getStates.bind(LocationsSvc),
      LocationsSvc.getStatesAlphabetHeaders.bind(LocationsSvc),
      LOCATION_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            ...breadcrumbs,
            {
              name: 'States',
              path: '#',
            },
          ],
          LOCATIONS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getCityData() {
    const { slugName, parentType } = this.props;
    this.parentTopic = await this.getParentTopic();

    const { breadcrumbs } = getLocationParentData(this.parentTopic, `${parentType}/${slugName}`);

    return [
      LocationsSvc.getCities.bind(LocationsSvc),
      LocationsSvc.getCitiesAlphabetHeaders.bind(LocationsSvc),
      LOCATION_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            ...breadcrumbs,
            {
              name: 'Cities',
              path: '#',
            },
          ],
          LOCATIONS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getAreaData() {
    const { slugName, parentType } = this.props;
    this.parentTopic = await this.getParentTopic();

    const { breadcrumbs } = getLocationParentData(this.parentTopic, `${parentType}/${slugName}`);

    return [
      LocationsSvc.getAreas.bind(LocationsSvc),
      LocationsSvc.getAreasAlphabetHeaders.bind(LocationsSvc),
      LOCATION_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            ...breadcrumbs,
            {
              name: 'Areas',
              path: '#',
            },
          ],
          LOCATIONS_PATH_ITEM,
        ),
      ),
    ];
  }

  async getPlaceData() {
    const { slugName, parentType } = this.props;
    this.parentTopic = await this.getParentTopic();

    const { breadcrumbs } = getLocationParentData(this.parentTopic, `${parentType}/${slugName}`);

    return [
      LocationsSvc.getPlaces.bind(LocationsSvc),
      LocationsSvc.getPlacesAlphabetHeaders.bind(LocationsSvc),
      LOCATION_BREADCRUMBS.concat(
        addPathOnBreadcrumbs(
          [
            ...breadcrumbs,
            {
              name: 'Places',
              path: '#',
            },
          ],
          LOCATIONS_PATH_ITEM,
        ),
      ),
    ];
  }

  async prepareData() {
    const {
      queryString,
      slugType,
      slugName,
      sectorSlug,
      industrySlug,
      subIndustrySlug,
      startupCategorySlug,
      parentType,
    } = this.props;

    this.setState({
      loading: true,
    });

    let filter;
    let alphabetHeadersMethod = () => { };
    let breadcrumbs = [];

    try {
      filter = getQueryStringParams(queryString).filter || null;
    } catch (e) {
      filter = null;
    }

    try {
      switch (slugType) {
        case TOPIC_CLASS:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await (
            this.getTopicClassData(slugName)
          ));
          break;
        case SECTOR_TOPIC_CLASS:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await (
            this.getSectorTopicClassData(slugName, sectorSlug)
          ));
          break;
        case INDUSTRY_TOPIC_CLASS:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await (
            this.getIndustryTopicClassData(slugName, sectorSlug, industrySlug)
          ));
          break;
        case SUB_INDUSTRY_TOPIC_CLASS:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await (
            this.getSubIndustryTopicClassData(slugName, sectorSlug, industrySlug, subIndustrySlug)
          ));
          break;
        case CATEGORY_TOPIC_CLASS:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await (
            this.getCategoryTopicClassData(
              slugName,
              sectorSlug,
              industrySlug,
              subIndustrySlug,
              startupCategorySlug,
            )
          ));
          break;
        case INDUSTRY_ONLY:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await (
            this.getIndustryOnlyData()
          ));
          break;
        case SUB_INDUSTRY_ONLY:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await (
            this.getSubIndustryOnlyData()
          ));
          break;
        case CATEGORY_ONLY:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await (
            this.getCategoryOnlyData()
          ));
          break;
        case INDUSTRY:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await (
            this.getIndustryData(slugName)
          ));
          break;
        case SUB_INDUSTRY:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await (
            this.getSubIndustryData(slugName, sectorSlug)
          ));
          break;
        case CATEGORY:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await (
            this.getCategoryData(slugName, sectorSlug, industrySlug)
          ));
          break;
        case LOCATION:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await this.getLocationData());
          break;
        case SUBCONTINENT:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await (
            this.getSubContinentData()
          ));
          break;
        case COUNTRY:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await this.getCountryData());
          break;
        case STATE:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await this.getStateData());
          break;
        case CITY:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await this.getCityData());
          break;
        case AREA:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await this.getAreaData());
          break;
        case PLACE:
          ([this.topicMethod, alphabetHeadersMethod, breadcrumbs] = await this.getPlaceData());
          break;
        default:
      }
    } catch (e) {
      if (typeof window === 'object') {
        window.location.pathname = '/404';
      }
    }

    if (!this.parentTopic) {
      window.location.pathname = '/404';
      return;
    }

    const { alphabetHeaders, totalItems } = await alphabetHeadersMethod({
      id: this.parentTopic.id,
      sectorId: this.sector && this.sector.id,
      industryId: this.industry && this.industry.id,
      subIndustryId: this.subIndustry && this.subIndustry.id,
      categoryId: this.category && this.category.id,
      slugName,
      ...(LOCATION_TYPES.includes(slugType) && {
        parent_type: slugType !== LOCATION ? parentType : '',
      }),
    });

    filter = filter || alphabetHeaders[0];
    const pageNumber = queryString ? parseInt(getQueryStringParams(queryString).page, 10) || 1 : 1;

    const { slug, assets, totalPages } = await this.topicMethod({
      id: this.parentTopic.id,
      sectorsId: this.sector && this.sector.id,
      industriesId: this.industry && this.industry.id,
      subIndustriesId: this.subIndustry && this.subIndustry.id,
      categoriesId: this.category && this.category.id,
      page: pageNumber,
      per_page: ITEMS_IN_PER_PAGE,
      prefix: filter,
      ...(LOCATION_TYPES.includes(slugType) && {
        parent_type: parentType,
        ...(slugType === LOCATION && {
          noParent: true,
        }),
      }),
    });

    assets.sort((obj1, obj2) => obj1.name.toLowerCase().localeCompare(obj2.name.toLowerCase()));

    if (this.mounted) {
      this.setState({
        slug: slug || this.parentTopic,
        assets,
        alphabetHeaders,
        filter,
        breadcrumbs,
        pageNumber,
        totalItems,
        totalPages: totalPages || 1,
        loading: false,
      });
    }
  }

  render() {
    const {
      slug,
      assets,
      filter,
      alphabetHeaders,
      totalPages,
      pageNumber,
      totalItems,
      breadcrumbs,
      loading,
    } = this.state;
    const {
      slugType,
      piwikEnabled,
      searchBarActive,
      childType,
    } = this.props;
    const {
      sector,
      industry,
      subIndustry,
      category,
      onPageSelect,
    } = this;

    if (!slug) {
      return (
        <div className={cx('loading')}>
          <Preloader loading />
        </div>
      );
    }

    return (
      <article
        className={cx('slugs-list', {
          'blured-background': searchBarActive,
        })}
      >
        <article>
          <Breadcrumbs items={breadcrumbs} />
          <SlugsListHeader
            slug={slug}
            type={slugType}
            sectorSlug={sector}
            industrySlug={industry}
            subIndustrySlug={subIndustry}
            categorySlug={category}
            piwikEnabled={piwikEnabled}
          />
          {slug && (
            <SlugsListBody
              slug={slug}
              slugs={assets}
              filter={filter}
              alphabetHeaders={alphabetHeaders}
              slugType={slugType}
              totalPages={totalPages}
              pageNumber={pageNumber}
              onPageSelect={onPageSelect}
              totalItems={totalItems}
              loading={loading}
              childType={childType}
            />
          )}
        </article>
      </article>
    );
  }
}

TopicClassSlugs.propTypes = {
  slugName: PropTypes.string.isRequired,
  slugType: PropTypes.oneOf([
    TOPIC_CLASS,
    SECTOR_TOPIC_CLASS,
    INDUSTRY_TOPIC_CLASS,
    SUB_INDUSTRY_TOPIC_CLASS,
    CATEGORY_TOPIC_CLASS,
    INDUSTRY_ONLY,
    SUB_INDUSTRY_ONLY,
    CATEGORY_ONLY,
    INDUSTRY,
    SUB_INDUSTRY,
    CATEGORY,
    LOCATION,
    CONTINENT,
    SUBCONTINENT,
    COUNTRY,
    STATE,
    CITY,
    AREA,
    PLACE,
  ]),
  queryString: PropTypes.string,
  sectorSlug: PropTypes.string,
  industrySlug: PropTypes.string,
  subIndustrySlug: PropTypes.string,
  startupCategorySlug: PropTypes.string,
  parentType: PropTypes.string,
  childType: PropTypes.string,
};

TopicClassSlugs.defaultProps = {
  slugType: TOPIC_CLASS,
  queryString: '',
  sectorSlug: '',
  industrySlug: '',
  subIndustrySlug: '',
  startupCategorySlug: '',
  parentType: '',
  childType: '',
};

const mapStateToProps = (state) => ({
  searchBarActive: state.searchReducers.searchBarActive,
});

export default withComponentName(connect(mapStateToProps)(TopicClassSlugs));
